import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { fetchCalibrationRevisions, fetchLoadTestRecord } from '../services/api';
import { useUnits } from '../load_test_components/units';
import { TEST_STATUS } from '../load_test_components/steps';
import AssignJobDialog from '../load_test_components/AssignJobDialog';
import { formatSavedAt, historyRevisionPath, testSummary, testTitle } from '../load_test_components/testRecord';

// Operations > Load Testing > Historical Data > a test: what the test was, and
// its calibration revisions, newest first. Opening a revision shows the test's
// dashboard converted with it; New revision opens the Calibration window there,
// starting from the revision in use.

// What a revision changed from the one before it, by section of the settings.
const SECTIONS = [
  ['loads', 'Test loads'],
  ['loadcell', 'Load cell'],
  ['lvdts', 'LVDTs'],
  ['strain_gauges', 'Strain gauges'],
];

function changedSections(revision, previous) {
  if (!previous) return null;
  const a = revision.settings || {};
  const b = previous.settings || {};
  return SECTIONS.filter(([key]) => JSON.stringify(a[key] ?? null) !== JSON.stringify(b[key] ?? null)).map(([, label]) => label);
}

function LoadTestHistoryTestPage() {
  const { testId: rawId } = useParams();
  const testId = Number(rawId);
  const navigate = useNavigate();
  const units = useUnits();
  const [test, setTest] = useState(null);
  const [revisions, setRevisions] = useState(null);
  const [error, setError] = useState(null);
  const [editingJob, setEditingJob] = useState(false);

  useEffect(() => {
    let current = true;
    setError(null);
    Promise.all([fetchLoadTestRecord(testId), fetchCalibrationRevisions(testId)])
      .then(([record, revs]) => {
        if (!current) return;
        setTest(record.test);
        setRevisions(revs.revisions || []);
      })
      .catch((err) => { if (current) setError(err.message); });
    return () => { current = false; };
  }, [testId]);

  // Oldest first for comparing each revision with the one before it.
  const byNumber = [...(revisions || [])].sort((a, b) => a.rev_number - b.rev_number);
  const active = (revisions || []).find((r) => r.is_active) || byNumber[byNumber.length - 1];
  const status = TEST_STATUS[test?.status];

  const openRevision = (revision, state) => navigate(historyRevisionPath(testId, revision.id), state ? { state } : undefined);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/operations/load-test/history')} sx={{ textTransform: 'none' }}>
          Historical Data
        </Button>
      </Box>

      {error ? (
        <Paper sx={{ p: 3, borderRadius: 1 }}><Typography color='error'>{error}</Typography></Paper>
      ) : (
        <>
          <Paper sx={{ p: 2, borderRadius: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ minWidth: 0, flex: '1 1 320px' }}>
              {test ? (
                <>
                  <Stack direction='row' alignItems='center' spacing={1} flexWrap='wrap' useFlexGap>
                    <Typography variant='h6' component='h1' fontWeight={600}>{testTitle(test)}</Typography>
                    {status && (
                      <Tooltip arrow title={status.description || ''}>
                        <Chip size='small' variant='outlined' color={status.color} label={status.label} />
                      </Tooltip>
                    )}
                  </Stack>
                  <Typography variant='body2' color='text.secondary'>{testSummary(test, units)}</Typography>
                  {test.location && <Typography variant='body2' color='text.secondary'>{test.location}</Typography>}
                </>
              ) : (
                <>
                  <Skeleton width='60%' height={32} />
                  <Skeleton width='80%' />
                </>
              )}
            </Box>
            <Stack direction='row' spacing={1}>
              <Button variant='outlined' startIcon={<EditOutlinedIcon />} disabled={!test} onClick={() => setEditingJob(true)} sx={{ textTransform: 'none' }}>
                {test?.job_number ? 'Edit job' : 'Assign job'}
              </Button>
              <Button variant='contained' startIcon={<ShowChartIcon />} disabled={!active} onClick={() => openRevision(active)} sx={{ textTransform: 'none' }}>
                Open dashboard
              </Button>
            </Stack>
          </Paper>

          <Paper sx={{ borderRadius: 1, overflow: 'hidden' }}>
            <Stack direction='row' alignItems='center' sx={{ px: 2, py: 1.5 }}>
              <Typography variant='subtitle1' component='h2' fontWeight={600}>Calibration revisions</Typography>
              <Button
                startIcon={<AddIcon />}
                disabled={!active}
                onClick={() => openRevision(active, { calibrate: true })}
                sx={{ ml: 'auto', textTransform: 'none' }}
              >
                New revision
              </Button>
            </Stack>
            <TableContainer>
              <Table size='small' sx={{ minWidth: 640 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 120 }}>Revision</TableCell>
                    <TableCell>Note</TableCell>
                    <TableCell>Changed</TableCell>
                    <TableCell>Saved by</TableCell>
                    <TableCell align='right' />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!revisions && [0, 1].map((i) => (
                    <TableRow key={i}><TableCell colSpan={5}><Skeleton animation='wave' height={32} /></TableCell></TableRow>
                  ))}
                  {revisions && revisions.map((r) => {
                    const previous = byNumber[byNumber.indexOf(r) - 1];
                    const changed = changedSections(r, previous);
                    return (
                      <TableRow key={r.id} hover onClick={() => openRevision(r)} sx={{ cursor: 'pointer' }}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant='body2' fontWeight={600} component='span'>Rev {r.rev_number}</Typography>
                          {r.is_active && <Chip size='small' color='primary' variant='outlined' label='In use' sx={{ ml: 1 }} />}
                        </TableCell>
                        <TableCell>{r.note || <Typography variant='body2' color='text.secondary' component='span'>—</Typography>}</TableCell>
                        <TableCell>
                          <Typography variant='body2' color='text.secondary'>
                            {changed === null ? 'Original' : changed.length ? changed.join(', ') : 'Nothing'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant='body2'>{r.created_by || '—'}</Typography>
                          <Typography variant='caption' color='text.secondary'>{formatSavedAt(r.created_at)}</Typography>
                        </TableCell>
                        <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                          <Button size='small' endIcon={<ShowChartIcon fontSize='small' />} sx={{ textTransform: 'none' }}>
                            Open dashboard
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      <AssignJobDialog
        open={editingJob}
        test={test}
        onClose={() => setEditingJob(false)}
        onSaved={(row) => { setTest(row); setEditingJob(false); }}
      />
    </Box>
  );
}

export default LoadTestHistoryTestPage;
